import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import type { EnrichedLead } from '@repo/contact-form-backend/src/lib/enrichment/enrichLead';
import type { ContactFormFields } from '@transcend-io/contact-form-schema';
import { snakeCase } from 'change-case';
import { AnimatePresence, motion } from 'framer-motion';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  FormProvider,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';
import type { ZodType } from 'zod';

import type { ResponseError } from '#/lib/api/types';
import {
  ChiliPiperProvider,
  useChiliPiperModal,
} from '#/lib/chili-piper/ChiliPiperContext';
import { scheduleAfterSubmit } from '#/lib/chili-piper/scheduleAfterSubmit';
import { useConsentManager } from '#/lib/consent-manager';
import { checkFormServerError } from '#/lib/contact-form-client/helpers';
import { submitLead } from '#/lib/contact-form-client/submit';
import type { SanityEnrichedForm } from '#/lib/sanity/queries/form';
import { useAnalytics, useConversionEvent } from '#/lib/segment';
import { cn } from '#/lib/utils';
import { Button } from '#/components/Button';

import LoadingSpinner from '../LoadingSpinner';
import { FormFields } from './Custom/FormFields';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './FormControls';
import { generateAnalyticsId, useFormMetadata } from './utils';

type Props<T extends ZodType<ContactFormFields>> = Pick<
  SanityEnrichedForm,
  | 'hubspotCampaignId'
  | 'hubspotConvertingFormType'
  | 'contentPieceTitle'
  | 'hubspotEmailSubscriptionIds'
  | 'trackingEventName'
  | 'submitButtonLabel'
  | 'fieldGroups'
  | 'formTitle'
> & {
  chiliPiperRouterId?: string | null;
  formFieldsSchema: T;
  ConfirmationComponent: React.FC;
  onSubmitSuccess?: () => void;
  onSubmitError?: ({ error }: ResponseError) => void;
  children: (ctx: UseFormReturn<ContactFormFields>) => React.ReactNode;
  strictEnrichment: boolean;
  trackingEventProperties: SanityEnrichedForm['trackingEventProperties'];
  theme?: 'light' | 'dark';
};

function RawForm<T extends ZodType<ContactFormFields>>({
  hubspotCampaignId,
  hubspotConvertingFormType,
  contentPieceTitle,
  hubspotEmailSubscriptionIds,
  chiliPiperRouterId,
  formFieldsSchema,
  trackingEventName,
  ConfirmationComponent,
  onSubmitSuccess,
  onSubmitError,
  children,
  fieldGroups,
  submitButtonLabel,
  strictEnrichment,
  trackingEventProperties,
  theme,
  formTitle,
}: Props<T>): JSX.Element {
  const { airgap } = useConsentManager();
  const { asPath } = useRouter();

  const [currentStep, setCurrentStep] = useState(0);
  const [stepEventsFired, setStepEventsFired] = useState<number[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formSubmissionId, setFormSubmissionId] = useState<string | undefined>(
    undefined,
  );
  const [enrichmentStatus, setEnrichmentStatus] = useState<{
    hasAttemptedEnrichment: boolean;
    enrichmentWasSuccessful: boolean;
    data: EnrichedLead | null;
  }>({
    hasAttemptedEnrichment: false,
    enrichmentWasSuccessful: false,
    data: null,
  });
  const { methods, getMetadata } = useFormMetadata({
    hubspotCampaignId,
    hubspotConvertingFormType,
    contentPieceTitle,
    hubspotEmailSubscriptionIds,
    trackingEventName,
    formFieldsSchema,
    formTitle: formTitle,
    path: asPath,
  });

  const analytics = useAnalytics();
  const fireConversionEvent = useConversionEvent();
  const { initSchedulingModal, waitForSchedulingFinished } =
    useChiliPiperModal();

  useEffect(() => methods.setValue('consent', true), [methods]);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const isLastStep = currentStep === fieldGroups.length - 1;
  const isFirstStep = currentStep === 0;
  const analyticsId = generateAnalyticsId(formTitle, asPath);

  const maybeSchedule = async (
    fields: ContactFormFields,
    submissionId: string,
  ) => {
    if (!chiliPiperRouterId) {
      return;
    }
    await scheduleAfterSubmit({
      fields: { ...fields, submissionId },
      submissionId,
      analyticsId,
      trackingEventName,
      analytics,
      getMetadata,
      executeRecaptcha,
      initSchedulingModal,
      waitForSchedulingFinished,
    });
  };

  const currentStepFields = fieldGroups[currentStep].fields
    .flatMap((field) => {
      if ('name' in field && field.name) {
        return [field.name];
      }
      if (field._type === 'countryRegion') {
        return ['country', 'state'];
      }
    })
    .filter((fieldName): fieldName is keyof ContactFormFields =>
      Boolean(fieldName),
    )
    .filter((fieldName) => fieldName !== 'email');

  const emailField = fieldGroups[0].fields.find(
    (field) => field._type === 'email',
  );

  /**
   * Handle back button click, which will also clear errors so it doesnt silently block submissions of the form on previous steps
   */
  const handleBack = async () => {
    // Trigger validation silently without focusing on errors
    const isValid = await methods.trigger(currentStepFields, {
      shouldFocus: false,
    });

    if (!isValid) {
      // Get errored fields in the current step
      const errors = methods.formState.errors;
      const erroredFieldsInCurrentStep = currentStepFields.filter(
        (fieldName) => errors[fieldName],
      );

      erroredFieldsInCurrentStep.forEach((fieldName) => {
        methods.resetField(fieldName, {
          keepError: false,
          keepDirty: false,
          keepTouched: false,
        });
      });
      setCurrentStep(currentStep - 1);
    }
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit: SubmitHandler<ContactFormFields> = async (fields) => {
    const formId = generateAnalyticsId(formTitle, asPath);
    try {
      // Recaptcha / submit first so Segment identify+track don't block paint.
      const recaptchaToken =
        executeRecaptcha &&
        (await executeRecaptcha(snakeCase(trackingEventName)));

      const attributedMetadata = await getMetadata(recaptchaToken);

      const metadata = {
        ...attributedMetadata,
        step: currentStep + 1,
        totalSteps: fieldGroups.length,
      };

      const response = await submitLead(
        { ...fields, submissionId: formSubmissionId },
        metadata,
        { analytics },
      );

      if ('error' in response) {
        console.error(response.error);
        methods.setError('root', {
          type: 'server',
          message: response.error,
        });
        const serverError = checkFormServerError(methods.formState);
        if (serverError) {
          onSubmitError?.({ error: serverError.message });
        }
      } else {
        const submissionId = response.submissionId;
        // Fire step event once per step after a successful submit.
        if (!stepEventsFired.includes(currentStep)) {
          void fireConversionEvent(
            fields.email,
            'Contact Form Step Submitted',
            {
              step: currentStep + 1,
              totalSteps: fieldGroups.length,
              isFinalStep: isLastStep,
              formId,
              submissionId,
            },
          );
          setStepEventsFired([...stepEventsFired, currentStep]);
        }
        if (isLastStep) {
          void fireConversionEvent(fields.email, trackingEventName, {
            ...trackingEventProperties,
            formId,
            submissionId,
          });
          await maybeSchedule(fields, submissionId);
          setShowConfirmation(true);
          onSubmitSuccess?.();
        } else {
          setFormSubmissionId(response.submissionId);
          setCurrentStep((prev) => prev + 1);
        }
      }
    } catch (err) {
      console.error(err);
      methods.setError('root', { type: 'server' });
      const serverError = checkFormServerError(methods.formState);
      if (serverError) {
        onSubmitError?.({ error: serverError.message });
      }
    }
  };

  const handleEnrichmentSubmit: SubmitHandler<ContactFormFields> = async (
    fields,
  ) => {
    const trackingPurposes = Object.fromEntries(
      Object.keys(airgap?.getConsent().purposes ?? {}).map((purpose) => [
        purpose,
        airgap?.isConsented(new Set([purpose])) ?? false,
      ]),
    );

    try {
      const recaptchaToken =
        executeRecaptcha &&
        (await executeRecaptcha(snakeCase(trackingEventName)));
      const metadata = await getMetadata(recaptchaToken);

      const response = await submitLead(
        fields,
        { ...metadata, shouldEnrich: true, trackingPurposes },
        { analytics },
      );

      if ('error' in response) {
        console.error(response.error);
        methods.setError('root', {
          type: 'server',
          message: response.error,
        });
        const serverError = checkFormServerError(methods.formState);
        if (serverError) {
          onSubmitError?.({ error: serverError.message });
        }
      } else {
        setEnrichmentStatus({
          hasAttemptedEnrichment: true,
          enrichmentWasSuccessful: !strictEnrichment
            ? true
            : (response.enriched?.success ?? false),
          // if strict enrichment is false, we only want the first step to be shown regardless of enrichment success
          data: response.enriched?.data ?? null,
        });
        analytics.track('Form Enrichment Attempted', {
          email: fields.email,
          wasSuccessful: response.enriched?.success ?? false,
          formId: generateAnalyticsId(formTitle, asPath),
          submissionId: response.submissionId,
        });
        if (response.enriched?.success) {
          await maybeSchedule(
            { ...fields, ...(response.enriched.data ?? {}) },
            response.submissionId,
          );
          setShowConfirmation(true);
          fireConversionEvent(fields.email, trackingEventName, {
            successfullyEnriched: true,
            enrichedFields: Object.keys(response.enriched.data ?? {}),
            formId: generateAnalyticsId(formTitle, asPath),
            submissionId: response.submissionId,
            ...trackingEventProperties,
          });
          onSubmitSuccess?.();
        } else if (!strictEnrichment) {
          fireConversionEvent(fields.email, trackingEventName, {
            successfullyEnriched: false,
            formId: generateAnalyticsId(formTitle, asPath),
            submissionId: response.submissionId,
            ...trackingEventProperties,
          });
          await maybeSchedule(fields, response.submissionId);
          setShowConfirmation(true);
          onSubmitSuccess?.();
        } else {
          // no enrichment, show the multistep form
          setFormSubmissionId(response.submissionId);
        }
      }
    } catch (err) {
      console.error(err);
      methods.setError('root', { type: 'server' });
      const serverError = checkFormServerError(methods.formState);
      if (serverError) {
        onSubmitError?.({ error: serverError.message });
      }
    }
  };

  const buttonLabel = isLastStep ? submitButtonLabel : 'Continue';

  return (
    <AnimatePresence mode="wait">
      {showConfirmation &&
        (enrichmentStatus.hasAttemptedEnrichment ||
          methods.formState.isSubmitSuccessful) && (
          <motion.div
            key="confirmation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ConfirmationComponent />
          </motion.div>
        )}
      {/*  Single input form flow */}
      {!enrichmentStatus.hasAttemptedEnrichment &&
        !methods.formState.isSubmitSuccessful &&
        emailField && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex-1 flex flex-col max-w-[520px] mx-auto m:mx-0 w-full"
            >
              <FormProvider {...methods}>
                <form
                  name={analyticsId}
                  onSubmit={methods.handleSubmit(handleEnrichmentSubmit)}
                  className={cn(
                    'm:form-input flex justify-between p-0 m:p-0 overflow-clip flex-col m:flex-row gap-y-10 m:gap-y-0',
                    theme === 'dark' && 'm:form-input-dark',
                  )}
                >
                  <FormField
                    key={emailField._key}
                    name={emailField.name}
                    control={methods.control}
                    defaultValue={''}
                    render={({ field: fieldProps }) => {
                      return (
                        <FormItem className="h-full flex-1">
                          <FormLabel className="sr-only">
                            {emailField.label}
                          </FormLabel>
                          <FormControl>
                            <input
                              type={'email'}
                              autoComplete="work email"
                              required={true}
                              placeholder={emailField.label}
                              className={cn(
                                'max-m:form-input size-full px-20 m:focus:outline-none m:border-none bg-transparent h-50',
                                theme === 'dark'
                                  ? 'max-m:form-input-dark autofill-override-dark'
                                  : 'autofill-override',
                              )}
                              {...fieldProps}
                            />
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />

                  <Button
                    variant="form"
                    formTheme={theme}
                    type="submit"
                    disabled={methods.formState.isSubmitting}
                    className={cn(
                      'grid place-items-center min-w-160  border-none md:rounded-r-none',
                      {
                        'bg-electric-06':
                          methods.formState.isSubmitting && theme === 'dark',
                      },
                      {
                        'bg-grayscale-06':
                          methods.formState.isSubmitting && theme !== 'dark',
                      },
                    )}
                  >
                    <span
                      className={cn(
                        'row-start-1 col-start-1',
                        methods.formState.isSubmitting && 'opacity-0',
                      )}
                    >
                      {submitButtonLabel}
                    </span>
                    {methods.formState.isSubmitting && (
                      <LoadingSpinner
                        formTheme={theme}
                        className={cn(
                          'row-start-1 col-start-1 absolute size-22',
                        )}
                      />
                    )}
                  </Button>
                </form>
                {/* Needs to be wapped in a form field to get the context that contains the error message */}
                <FormField
                  name={emailField.name}
                  control={methods.control}
                  render={() => (
                    <FormMessage className="legal-text mt-4 text-flame-06" />
                  )}
                />
                <div className="col-span-full">{children(methods)}</div>
              </FormProvider>
            </motion.div>
          </>
        )}
      {/* Multistep form flow when enrichment fails */}
      {enrichmentStatus.hasAttemptedEnrichment &&
        !enrichmentStatus.enrichmentWasSuccessful &&
        !showConfirmation && (
          <>
            <div className="flex gap-12 items-center justify-between mb-20 col-span-full">
              {fieldGroups[currentStep].title ? (
                <h3 className="text-18 font-[440]">
                  {fieldGroups[currentStep].title}
                </h3>
              ) : (
                <span />
              )}
              {fieldGroups.length > 1 ? (
                <span
                  className={cn(
                    'step-indicator font-mono text-12 font-[400]',
                    theme === 'dark' ? 'text-cloud' : 'text-grayscale-03',
                  )}
                >
                  STEP{' '}
                  <motion.span
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1 }}
                    exit={{ y: -10, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {currentStep + 1}
                  </motion.span>
                  /{fieldGroups.length}
                </span>
              ) : (
                <span />
              )}
            </div>
            <motion.div
              key={`form-step-${currentStep}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <FormProvider {...methods}>
                <form
                  name={analyticsId}
                  onSubmit={methods.handleSubmit(handleSubmit)}
                  className="grid grid-cols-2 gap-20"
                >
                  <FormFields
                    theme={theme}
                    autoFocusFirstField={!isFirstStep}
                    fields={fieldGroups[currentStep].fields}
                    control={methods.control}
                  />

                  <div className="col-span-full flex flex-row-reverse justify-between">
                    <Button
                      formTheme={theme}
                      variant="form"
                      type="submit"
                      disabled={methods.formState.isSubmitting}
                      className={cn('grid place-items-center min-w-120')}
                    >
                      <span
                        className={cn(
                          'row-start-1 col-start-1',
                          methods.formState.isSubmitting && 'opacity-0',
                        )}
                      >
                        {buttonLabel}
                      </span>
                      {methods.formState.isSubmitting && (
                        <LoadingSpinner
                          formTheme={theme}
                          className={cn(
                            'row-start-1 col-start-1 absolute size-22',
                          )}
                        />
                      )}
                    </Button>
                    {!isFirstStep && (
                      <Button
                        variant="tertiary"
                        isBackLink
                        onClick={handleBack}
                        type="button"
                        className="min-w-120"
                      >
                        Back
                      </Button>
                    )}
                  </div>
                  {/* Legal Text And Error Message */}
                  <div className="col-span-full">{children(methods)}</div>
                </form>
              </FormProvider>
            </motion.div>
          </>
        )}
    </AnimatePresence>
  );
}

export function EnrichedForm<T extends ZodType<ContactFormFields>>(
  props: Props<T>,
) {
  const chiliPiperRouterId = props.chiliPiperRouterId?.trim() || null;

  return (
    <ChiliPiperProvider
      enableScheduling={Boolean(chiliPiperRouterId)}
      routerId={chiliPiperRouterId}
    >
      <RawForm {...props} chiliPiperRouterId={chiliPiperRouterId} />
    </ChiliPiperProvider>
  );
}
